<script setup lang="ts">
import { Inbox01, ChevronDown, Eye, EyeOff, Check } from "utruckin-icons";
import { ITableHeader, ITableProps } from "@/types/table";
import CustomLoading from "@/components/custom/CustomLoading.vue";
import { computed, nextTick, onBeforeMount, onMounted, ref } from "vue";
import { isEqual } from "lodash-es";

interface ITableGroup {
  label: string;
  sublabel?: string;
  count?: number;
  items: any[];
}

interface ITablePropsExtended extends ITableProps {
  groups?: ITableGroup[];
  visibility?: boolean;
  isTaskTable?: boolean;
  selectMode: boolean;
  dateIndex?: number;
  shiftIndex?: number;
  isAdmin?: boolean;
  withoutHeader?: boolean;
  withoutShift?: boolean;
  stickyColumns?: number;
}

const props = withDefaults(defineProps<ITablePropsExtended>(), {
  loading: false,
  headers: undefined,
  items: undefined,
  groups: undefined,
  striped: true,
  visibility: true,
  isTaskTable: false,
  selectMode: false,
  isAdmin: false,
  withoutHeader: false,
  withoutShift: false,
  stickyColumns: 0,
});

const emits = defineEmits<{
  (e: "update:selected", value: any): void;
  (e: "select", row: any): void;
  (e: "deselect", row: any): void;
  (e: "row-click", item: any, rowIdx: number): void;
  (e: "row-db-click", item: any, rowIdx: number): void;
}>();

const uid = ref("");
const collapsedGroups = ref<Set<number>>(new Set());
const selectedRecords = ref<any[]>([]);
const lastSelectedIdx = ref<number>();
const selectHeader = {
  name: "id",
  label: "No",
  fitContent: true,
  type: "centert",
};
const scrollToRow = async (id: number | string) => {
  await nextTick();

  const element = document.getElementById(`row-${id}`);

  if (element) {
    element.scrollIntoView({
      block: "center",
    });
  }
};

defineExpose({
  scrollToRow,
});

const SELECT_COL_WIDTH = 48;

const parsePx = (value?: string) => {
  if (!value) return 120; // fallback if a sticky column has no explicit width
  const num = parseFloat(value);
  return Number.isNaN(num) ? 120 : num;
};

const isSticky = (index: number) =>
  !!props.stickyColumns && index < props.stickyColumns;

const getStickyLeft = (index: number) => {
  let left = props.selectMode ? SELECT_COL_WIDTH : 0;
  for (let i = 0; i < index; i++) {
    left += parsePx(props.headers?.[i]?.width);
  }
  return `${left}px`;
};

const getColumnStyle = (
  header: ITableHeader,
  index?: number,
  cellType: "th" | "td" = "td"
) => {
  const style: Record<string, string> = {};

  if (header.fitContent) {
    style.width = "5%";
    style.whiteSpace = "nowrap";
  } else if (header.width) {
    style.width = header.width;
  } else {
    style.width = "auto";
  }

  if (index !== undefined && isSticky(index)) {
    style.position = "sticky";
    style.left = getStickyLeft(index);
    style.zIndex = cellType === "th" ? "3" : "1";
    if (cellType === "td") style.background = "inherit";
  }

  return style;
};

const selectColStyle = computed(() =>
  props.selectMode && props.stickyColumns
    ? { position: "sticky", left: "0px", zIndex: "3" }
    : {}
);

const selectColBodyStyle = computed(() =>
  props.selectMode && props.stickyColumns
    ? { position: "sticky", left: "0px", zIndex: "1", background: "inherit" }
    : {}
);

const selectedModel = computed({
  get() {
    return selectedRecords.value;
  },
  set(value) {
    emits("update:selected", value);
    selectedRecords.value = value;
  },
});

const isAllSelected = computed(() => {
  if (props.items?.length === selectedModel.value?.length) {
    return props.items?.every((row, idx) =>
      isEqual(row, selectedModel.value?.at(idx))
    );
  }

  return false;
});

function toggleGroup(idx: number) {
  if (collapsedGroups.value.has(idx)) {
    collapsedGroups.value.delete(idx);
  } else {
    collapsedGroups.value.add(idx);
  }
  collapsedGroups.value = new Set(collapsedGroups.value);
}

const isCollapsed = (idx: number) => collapsedGroups.value.has(idx);

const selectAll = (event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;

  if (checked) {
    selectedModel.value = [...(props.items || [])];
  } else {
    selectedModel.value = [];
  }
};

const rangeSelect = (row: any, idx: number) => {
  if (lastSelectedIdx.value !== -1) {
    const start = Math.min(lastSelectedIdx.value!, idx) + 1;
    const end = Math.max(lastSelectedIdx.value!, idx);
    for (let i = start; i <= end; i++) {
      const value = props.items[i];
      selectedModel.value?.push(value);
    }
  }
};

const onChange = (row: any, rowIdx: number, event: Event) => {
  const checked = (event.target as HTMLInputElement).checked;
  if (checked) {
    lastSelectedIdx.value = rowIdx;
    emits("select", row);
  } else emits("deselect", row);
};

const isSelected = (item: any) => {
  return selectedModel.value.some((row: any) => row.id === item.id);
};

const rowClick = (item: any, index: number) => {
  emits("row-click", item, index);
};

const rowDbClick = (item: any, index: number) => {
  emits("row-db-click", item, index);
};

onBeforeMount(() => {
  uid.value = crypto.randomUUID();
});

onMounted(async () => {
  await nextTick();
});
</script>

<template>
  <div class="c-co-table">
    <table class="table" :class="{ striped: striped }">
      <colgroup>
        <col v-if="selectMode" :style="getColumnStyle(selectHeader)" />
        <col
          v-for="(header, index) in headers"
          :key="index"
          :style="getColumnStyle(header)"
        />
      </colgroup>
      <thead v-if="!withoutHeader">
        <tr>
          <template v-if="selectMode">
            <th
              v-if="items?.length"
              class="ui-table-select-col"
              :style="selectColStyle"
            >
              <label
                class="custom__checkbox"
                :class="isAllSelected ? 'active' : ''"
              >
                <input
                  class="ui-checkbox form-checkbox"
                  type="checkbox"
                  :name="uid"
                  :checked="isAllSelected"
                  @click.stop
                  @change="selectAll"
                />

                <Check :size="14" />
              </label>
            </th>
            <th v-else></th>
          </template>

          <th
            v-for="(item, index) in headers"
            :key="index"
            :style="getColumnStyle(item, index, 'th')"
            :class="{
              'sticky-col-last': isSticky(index) && index === stickyColumns - 1,
            }"
          >
            <div class="table__head">
              <span class="table__head--label" v-html="item.label"></span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="groups && groups.length">
          <template v-for="(group, groupIdx) in groups" :key="groupIdx">
            <tr class="row--group-header">
              <td :colspan="headers?.length">
                <div class="group-header-inner">
                  <span class="group-badge">{{ group.label }}</span>
                  <span v-if="group.sublabel" class="group-time">
                    {{ group.sublabel }}
                  </span>
                  <span v-if="group.count !== undefined" class="group-count">
                    {{ group.count }} Users
                  </span>
                  <button
                    v-if="isCollapsed(groupIdx)"
                    class="group-toggle"
                    @click.stop="toggleGroup(groupIdx)"
                  >
                    <Eye :size="20" />
                  </button>
                  <button
                    v-else
                    class="group-toggle"
                    @click.stop="toggleGroup(groupIdx)"
                  >
                    <EyeOff :size="20" />
                  </button>
                </div>
              </td>
            </tr>
            <template v-if="!isCollapsed(groupIdx)">
              <template v-if="!collapsedGroups.has(groupIdx)">
                <tr
                  v-if="
                    dateIndex === 0 &&
                    shiftIndex == 0 &&
                    isTaskTable &&
                    !isAdmin
                  "
                  class="row--new-task"
                >
                  <slot name="new-row" />
                </tr>
                <tr
                  v-for="(item, rowIdx) in group.items"
                  :key="`${groupIdx}-${rowIdx}`"
                  :class="{
                    'row--inactive': item.is_active === false,
                    [item.status]: !!item.status,
                  }"
                  @click="rowClick(item, rowIdx)"
                  @dblclick="rowDbClick(item, rowIdx)"
                >
                  <td
                    v-for="(cellItem, cellIdx) in headers"
                    :key="cellIdx"
                    :class="[
                      cellItem.type == 'error' ? 'error' : '',
                      cellItem.type == 'day' ? 'day-cell-td' : '',
                      isSticky(cellIdx) && cellIdx === stickyColumns - 1
                        ? 'sticky-col-last'
                        : '',
                    ]"
                    :style="getColumnStyle(cellItem, cellIdx, 'td')"
                  >
                    <slot :name="cellItem.name" :item="item" :index="rowIdx">
                      {{ item[cellItem.name] }}
                    </slot>
                  </td>
                </tr>
              </template>
              <tr
                v-if="!group.items.length && !collapsedGroups.has(groupIdx)"
                class="row--empty-group"
              >
                <td :colspan="headers?.length">
                  <span class="group-empty">No users in this shift</span>
                </td>
              </tr>
            </template>
          </template>
        </template>

        <template v-else-if="items && items.length">
          <tr
            v-if="dateIndex === 0 && isTaskTable && !isAdmin"
            class="row--new-task"
          >
            <slot name="new-row" />
          </tr>
          <tr
            v-for="(item, rowIdx) in items"
            :key="rowIdx"
            :id="`row-${item.id}`"
            :class="{
              'row--inactive': item.is_active === false,
              [item.status]: !!item.status,
            }"
            @click="rowClick(item, rowIdx)"
            @dblclick="rowDbClick(item, rowIdx)"
          >
            <template v-if="selectMode">
              <td :style="selectColBodyStyle">
                <label
                  class="custom__checkbox"
                  :class="isSelected(item) ? 'active' : ''"
                >
                  <input
                    v-model="selectedModel"
                    :value="item"
                    class="ui-checkbox form-checkbox"
                    type="checkbox"
                    :name="uid"
                    @click.shift.exact="rangeSelect(item, rowIdx)"
                    @change="onChange(item, rowIdx, $event)"
                  />

                  <Check :size="14" />
                </label>
              </td>
            </template>

            <td
              v-for="(cellItem, cellIdx) in headers"
              :key="cellIdx"
              :class="[
                cellItem.type == 'error' ? 'error' : '',
                cellItem.type == 'day' ? 'day-cell-td' : '',
                isSticky(cellIdx) && cellIdx === stickyColumns - 1
                  ? 'sticky-col-last'
                  : '',
              ]"
              :style="getColumnStyle(cellItem, cellIdx, 'td')"
            >
              <slot :name="cellItem.name" :item="item" :index="rowIdx">
                {{ item[cellItem.name] }}
              </slot>
            </td>
          </tr>
        </template>

        <template v-else>
          <tr>
            <td
              class="no__data"
              :colspan="selectMode ? headers?.length + 1 : headers?.length"
            >
              <div class="no__data--body">
                <Inbox01 :size="40" />
                Data not found
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <!--    <template v-else>-->
    <!--      <CustomLoading-->
    <!--        v-for="n in 7"-->
    <!--        height="54px"-->
    <!--        width="100%"-->
    <!--        :key="n"-->
    <!--        style="margin-bottom: 8px"-->
    <!--      />-->
    <!--    </template>-->
  </div>
</template>

<style lang="scss">
.c-co-table {
  width: 100%;
  margin-top: var(--base-space-16);
  overflow: auto;

  table {
    height: 100%;
    width: 100%;
    table-layout: fixed;
    border-bottom: 1px solid var(--color-border);
    border-left: 1px solid var(--color-border);
    border-right: 1px solid var(--color-border);
    border-radius: var(--base-radius-16);
    border-spacing: 0;
    border-collapse: collapse;

    &.striped tbody tr:nth-child(odd) {
      background-color: var(--color-table-row);
    }

    thead {
      width: 100%;
      border-radius: var(--base-radius-16);
      border: 1px solid var(--color-border);
      position: sticky;
      z-index: 2;
      top: 0;
      background: var(--color-system-background);

      th {
        font-size: 14px;
        font-weight: 600;
        line-height: var(--base-space-20);
        border: 1px solid var(--color-border);
        text-align: left;
        color: var(--color-label-secondary);
        background: var(--color-system-background) !important;
        padding: 11px;
        text-wrap: nowrap;

        &:first-child {
          border-top-left-radius: var(--base-radius-16);
        }

        &:last-child {
          border-top-right-radius: var(--base-radius-16);
        }

        .table__head {
          display: flex;
          justify-content: start;
          align-items: center;
          gap: 4px;

          &--action {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: start;
          }
        }

        &.ui-table-select-col {
          text-align: center;
        }
      }
    }

    tbody {
      tr {
        cursor: pointer;

        &:hover {
          background: var(--color-bg-secondary);
        }

        td.center-td {
          text-align: center;
        }

        td {
          padding: 10px 12px;
          color: var(--color-label);
          font-size: 14px;
          line-height: var(--base-space-20);
          font-weight: 500;
          border: 1px solid var(--color-border);
          background: var(--color-system-background) !important;

          &:first-child {
            text-align: center;
          }

          &.error {
            color: var(--color-red-light);
          }

          .no__data {
            &--body {
              display: flex;
              flex-direction: column;
              width: 100%;
              justify-content: center;
              align-items: center;
              font-size: 18px;
              line-height: var(--base-space-24);
            }
          }
        }

        &:last-child td:first-child {
          border-bottom-left-radius: var(--base-radius-16);
        }

        &:last-child td:last-child {
          border-bottom-right-radius: var(--base-radius-16);
        }

        &.row--inactive td {
          color: var(--color-table-inactive) !important;
        }

        &.inform td {
          background: var(--color-grey-light) !important;
          color: var(--color-system-red) !important;
        }

        &.qa_failed td {
          background: var(--color-red-light) !important;
        }

        &.on_hold td {
          background: var(--color-yellow-light) !important;
        }

        &.row--group-header {
          cursor: default;

          &:hover {
            background: transparent;
          }

          td {
            background: var(--color-secondary-system-background);
            padding: 8px 12px;
            border-bottom: 1px solid var(--color-border);
          }
        }

        &.row--empty-group {
          cursor: default;

          &:hover {
            background: transparent;
          }

          td {
            padding: 10px 16px;
          }
        }
      }

      &.row--new-task {
        cursor: default;
        background: #eef4ff !important;

        &:hover {
          background: #eef4ff !important;
        }

        td {
          padding: 8px 10px;
          border-bottom: 2px solid var(--color-system-blue);
        }
      }
    }
  }

  .custom__checkbox {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    width: 20px;
    height: 20px;
    border-radius: 8px;
    border: 1px solid var(--color-border);
    color: transparent;

    &.active {
      background: var(--color-primary);
      color: var(--color-white);
      border-color: var(--color-primary);
    }

    input {
      display: none;
    }
  }

  .group-header-inner {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .group-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: var(--base-radius-12);
    background-color: var(--color-system-blue);
    color: var(--color-white);
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    text-transform: capitalize;
  }

  .group-time {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: var(--base-radius-12);
    border: 1px solid var(--color-system-blue);
    background: transparent;
    color: var(--color-system-blue);
    font-size: 13px;
    font-weight: 500;
  }

  .group-count {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: var(--base-radius-12);
    border: 1px solid var(--color-system-blue);
    background: transparent;
    color: var(--color-system-blue);
    font-size: 13px;
    font-weight: 500;
  }

  td.day-cell-td {
    padding: 0px !important;

    span {
      border-radius: 0;
    }
  }

  .group-toggle {
    margin-left: auto;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-system-blue);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
    }
  }

  .group-empty {
    font-size: 13px;
    color: var(--color-label-secondary);
    font-style: italic;
  }

  .group-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .group-toggle-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--color-primary);
    display: flex;
    align-items: center;
    padding: 2px;

    &:hover {
      opacity: 0.7;
    }
  }

  .sticky-col-last {
    box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--color-border);
  }
}
</style>
